// Пользователи и права: экран, которым администратор настраивает систему под заказчика.
//
// Без него систему настраивают запросами к серверу, то есть не настраивают вовсе: у
// заказчика администратор — это финансовый директор, а не программист.
//
// Роли и объекты заменяются ЦЕЛИКОМ, и экран устроен так же: человек видит полный набор и
// снимает отметку. Кнопки «добавить роль» нет намеренно — снятая роль обязана исчезать, а
// добавление «сверху» означало бы, что человек продолжает видеть забранное.

import { useState } from 'react';

import { RiSmartphoneLine, RiUserAddLine } from '@remixicon/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PrintButton } from '@/shared/reports/PrintButton';
import { КнопкаКопирования } from '@/shared/ui/copy-button';
import { МенюДействий } from './МенюДействий';
import { УдалениеУчётнойЗаписи } from './УдалениеУчётнойЗаписи';
import type { RoleOut, UserOut } from '@/shared/api/endpoints/users';
import type { BulkInviteOut } from '@/shared/api/endpoints/users';
import {
  createUser,
  deleteUser,
  issueInvite,
  issueInvitesBulk,
  revokeInvite,
  patchUser,
  setUserBotChats,
  setUserRoles,
  setUserSites,
} from '@/shared/api/endpoints/users';
import { ключиУчётных, useРоли, useУчётныеЗаписи } from '@/shared/users/queries';
import { useSitesQuery } from '@/shared/org/queries';
import { formatDateOnly } from '@/shared/time/format';
import { useЛюдиДляВыбора } from '@/shared/people/lookup';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { ApiError } from '@/shared/errors/api-error';
import { Block } from '@/shared/ui/block';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { cn } from '@/utils/cn';
import * as Button from '@/shared/ui/button';
import * as Checkbox from '@/shared/ui/checkbox';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { ListSkeleton } from '@/shared/ui/list-row';
import { ScreenState } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

/** Запись убрана из системы: в списке её нет, пока не попросить убранных отдельно. */
function убрана(пользователь: UserOut): boolean {
  return пользователь.archived_at !== null && пользователь.archived_at !== undefined;
}

/** Право менять учётные записи: имя, фраза для входа, карточка сотрудника, роли. */
const ПРАВО_МЕНЯТЬ = 'core.user.manage';

/**
 * Право открыть или закрыть человеку вход. Заведено 13.09.2026 под решение владельца: доступы
 * раздают руководитель и операционное руководство, а менять чужие данные им нельзя — `manage`
 * несёт смену фразы, то есть возможность войти под финансовым директором.
 */
const ПРАВО_ДОСТУПА = 'core.user.access';

/** Право выдать и погасить код для телефона. */
const ПРАВО_КОДОВ = 'core.user.invite';

/**
 * Право убрать учётную запись из системы. С 13.09.2026 только у администратора: «любые
 * действия с удалением только у админа».
 */
const ПРАВО_УДАЛЯТЬ = 'core.user.delete';

/**
 * Служебные переписки: код учётной записи бота и как эта переписка называется у человека.
 *
 * Список здесь, а не с сервера, ровно по одной причине: названий у ботов в базе нет — там логин
 * («avito») и отображаемое имя чата. Человеку же нужно понять, ЧТО он включает, и для этого
 * одного слова мало.
 */
const СЛУЖЕБНЫЕ_ПЕРЕПИСКИ: ReadonlyArray<{ код: string; название: string }> = [
  { код: 'avito', название: 'Авито: вопросы и разговоры' },
  { код: 'site_leads', название: 'Заявки с сайта' },
];

/**
 * Выбор карточки сотрудника для учётной записи.
 *
 * ## Зачем это существует
 *
 * Сервер принимает связь с карточкой при заведении и правке с первого дня, а поставить её было
 * НЕЧЕМ: форма её не отправляла, а правка звалась только с признаком «доступ открыт». Найдено
 * аудитом 06.09.2026, и это самое дорогое из найденного.
 *
 * Без связи система не знает, кто этот человек в реестре. Значит охранник входит по коду и не
 * видит своего расчётного листа — той единственной вещи, ради которой он в системе и есть, — а
 * старший объекта не видит своего подотчёта. Приёмочные проверки этого не ловили: связь в них
 * создаётся прямо в базе.
 *
 * Поиск отдельным полем, а не «печатай прямо в списке»: у заказчика 281 человек, и список
 * показывает первые пятьдесят по фамилии.
 */
function ВыборКарточки({
  id,
  значение,
  onВыбор,
  подпись = 'Карточка сотрудника',
  hint,
}: {
  id: string;
  значение: string;
  onВыбор: (employee_id: string) => void;
  подпись?: string;
  hint?: string;
}) {
  const [поиск, setПоиск] = useState('');
  const люди = useЛюдиДляВыбора(поиск === '' ? undefined : поиск);
  return (
    <div className='flex flex-col gap-2'>
      <TextField
        id={`${id}-поиск`}
        label='Поиск по фамилии'
        value={поиск}
        onChange={setПоиск}
        placeholder='Старцев'
        hint='Показываются первые пятьдесят по фамилии.'
      />
      <SelectField
        id={id}
        label={подпись}
        value={значение}
        onChange={onВыбор}
        options={(люди.data?.items ?? []).map((человек) => ({
          value: человек.id,
          label: человек.display_name,
        }))}
        placeholder='без карточки'
        pending={люди.isPending}
        failed={люди.isError}
        failedError={люди.error as ApiError | null}
        emptyNote='По этой фамилии никого не нашлось. Проверьте написание либо очистите поиск.'
        hint={hint}
      />
    </div>
  );
}

export function UsersPanel() {
  const клиент = useQueryClient();
  // Право читается ЗДЕСЬ, а не только на входе в раздел. Панель открывается по праву
  // просмотра, а внутри до 06.09.2026 не было ни одного гейта: руководство, которому по
  // матрице положен только просмотр, видело полностью рабочий экран — заводило людей, снимало
  // роли, выдавало коды. Нажатие давало красный отказ, и генеральный директор читал это как
  // поломку программы. Ровно тот анти-паттерн наоборот, который запрещает правило изоляции:
  // кнопка есть, маршрута нет.
  const права = useAuthStore((состояние) => состояние.permissions);
  const мойИдент = useAuthStore((состояние) => состояние.user?.id ?? null);
  const можноМенять = can(права, ПРАВО_МЕНЯТЬ);
  // Три права вместо одного: у операционного руководства есть доступы и коды, но нет правки
  // данных, и экран обязан показать ему ровно его кнопки. Одно общее «можно менять» либо
  // спрятало бы от него всё, либо показало бы то, что ответит красным отказом.
  const можноДоступ = can(права, ПРАВО_ДОСТУПА);
  const можноКоды = can(права, ПРАВО_КОДОВ);
  const можноУдалять = can(права, ПРАВО_УДАЛЯТЬ);
  /** Есть ли вообще хоть одно действие: по этому признаку показывается полоса кнопок. */
  const естьДействия = можноМенять || можноДоступ || можноКоды;
  /** Показывать ли убранные. По умолчанию нет: их убирают затем, чтобы они ушли с глаз. */
  const [показатьУбранных, setПоказатьУбранных] = useState(false);
  /** Кого сейчас подтверждают убрать. Окно на панели одно, а не по штуке в каждой строке. */
  const [удаляем, setУдаляем] = useState<UserOut | null>(null);
  const [правимКарточку, setПравимКарточку] = useState<string | null>(null);
  const [правимФразу, setПравимФразу] = useState<string | null>(null);
  const [новаяФраза, setНоваяФраза] = useState('');
  // Раздача кодов пачкой: отдельный режим, а не галочки поверх обычного списка. Список
  // читают каждый день, а коды раздают в день заезда вахты — постоянные галочки в каждой
  // строке были бы рябью ради действия, которое делают раз в два месяца.
  const [раздаём, setРаздаём] = useState(false);
  const [отмечены, setОтмечены] = useState<string[]>([]);
  const [пачка, setПачка] = useState<BulkInviteOut | null>(null);
  // Ключ выборки несёт признак «с убранными»: два состояния списка не должны затирать друг
  // друга в кэше. Общий корень при этом сохранён, поэтому `invalidateQueries` по нему гасит
  // оба разом — иначе после удаления второй список показывал бы вчерашнюю правду.
  // Список и справочник ролей берутся ОБЩИМ хуком: те же данные показывает вкладка «Доступ» в
  // карточке человека, и второй ключ кэша на один ответ означал бы, что снятая здесь роль
  // ещё полминуты видна там (`shared/users/queries.ts`).
  const ключСписка = ключиУчётных.список(показатьУбранных);
  const пользователи = useУчётныеЗаписи(показатьУбранных);
  const роли = useРоли();
  const объекты = useSitesQuery({});
  const [открыта, setОткрыта] = useState(false);
  /**
   * Выданный код держится ВМЕСТЕ с опознавателем человека: он показывается в его строке, а не
   * общей плашкой наверху панели. До 13.09.2026 плашка стояла над списком, и владелец, нажавший
   * «новый код» у последней строки, кода не увидел вовсе — тот нарисовался за пределами экрана.
   * Тот же дефект был найден и починен в приложении (`ios/.../Access/AccessScreen.swift`).
   */
  const [код, setКод] = useState<{
    id: string;
    код: string;
    ссылка: string;
  } | null>(null);
  const [отозвано, setОтозвано] = useState<{
    id: string;
    имя: string;
    сколько: number;
  } | null>(null);
  /**
   * Отказ действия рисуется В СТРОКЕ, у которой нажали, а не общей плашкой над списком.
   *
   * 13.09.2026 так же терялся и удачный ответ: владелец нажал «новый код» у нижней строки
   * длинного списка и не увидел кода — тот нарисовался выше границы экрана. Код в тот же день
   * переехал в строку, а отказ остался наверху, то есть дефект жил дальше и в худшем виде:
   * человек не видит ни результата, ни причины, по которой его нет.
   */
  const [сбой, setСбой] = useState<{ id: string; error: unknown } | null>(null);
  /** У кого сейчас раскрыта сетка ролей. Один за раз: две раскрытые дают ту же рябь. */
  const [правимРоли, setПравимРоли] = useState<string | null>(null);
  /**
   * Почему код не выдали. Живёт В СТРОКЕ того, у кого нажали, как отказ и итог отзыва.
   *
   * Случай ровно один и он частый: у человека нет ни одной роли. Код без роли пускает его в
   * систему, где ему не видно НИЧЕГО, — и читается это как поломка программы, а не как
   * недоделанная настройка. Поэтому первая выдача идёт через выбор роли.
   */
  const [сначалаРоль, setСначалаРоль] = useState<{ id: string; текст: string } | null>(null);

  const обновить = () => клиент.invalidateQueries({ queryKey: ключиУчётных.все });

  const назначение = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) => setUserRoles(id, roles),
    // Отметка гаснет сразу, не дожидаясь сервера. Это не оптимизм ради красоты: отметка,
    // которая полсекунды остаётся включённой, читается как «не сработало», и человек жмёт
    // второй раз. При отказе список возвращается к прежнему виду, а ошибка показывается.
    onMutate: async ({ id, roles }) => {
      setСбой(null);
      await клиент.cancelQueries({ queryKey: ключСписка });
      const прежний = клиент.getQueryData<{ items: UserOut[]; total: number }>(ключСписка);
      if (прежний !== undefined) {
        клиент.setQueryData(ключСписка, {
          ...прежний,
          items: прежний.items.map((человек) =>
            человек.id === id ? { ...человек, roles } : человек,
          ),
        });
      }
      return { прежний };
    },
    onError: (ошибка, переменные, контекст) => {
      if (контекст?.прежний !== undefined) {
        клиент.setQueryData(ключСписка, контекст.прежний);
      }
      setСбой({ id: переменные.id, error: ошибка });
    },
    onSettled: обновить,
  });
  // Служебные переписки: помощник, «Авито», заявки с сайта.
  //
  // Отдельно от ролей, потому что это РАЗНЫЕ вещи. Роль даёт право на данные, переписка — место,
  // куда эти данные приходят. До 11.09.2026 второе было следствием первого, и человек, которому
  // открыли настройку помощника, начинал получать вопросы соискателей в телефон.
  const перепискиЧеловека = useMutation({
    mutationFn: ({ id, chats }: { id: string; chats: string[] }) => setUserBotChats(id, chats),
    onMutate: async ({ id, chats }) => {
      setСбой(null);
      await клиент.cancelQueries({ queryKey: ключСписка });
      const прежний = клиент.getQueryData<{ items: UserOut[]; total: number }>(ключСписка);
      if (прежний !== undefined) {
        клиент.setQueryData(ключСписка, {
          ...прежний,
          items: прежний.items.map((человек) =>
            человек.id === id ? { ...человек, bot_chats: chats } : человек,
          ),
        });
      }
      return { прежний };
    },
    onError: (ошибка, переменные, контекст) => {
      if (контекст?.прежний !== undefined) {
        клиент.setQueryData(ключСписка, контекст.прежний);
      }
      setСбой({ id: переменные.id, error: ошибка });
    },
    onSettled: обновить,
  });
  const объектыЧеловека = useMutation({
    mutationFn: ({ id, sites }: { id: string; sites: string[] }) => setUserSites(id, sites),
    onMutate: () => setСбой(null),
    onError: (ошибка, переменные) => setСбой({ id: переменные.id, error: ошибка }),
    onSuccess: обновить,
  });
  const включение = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      patchUser(id, { is_active: active }),
    onMutate: () => setСбой(null),
    onError: (ошибка, переменные) => setСбой({ id: переменные.id, error: ошибка }),
    onSuccess: обновить,
  });
  // Задать человеку новую фразу.
  //
  // Единственный способ помочь тому, кто свою забыл: восстановления «по почте» в системе нет
  // и не будет — почты у охранника может не быть вовсе. До 09.09.2026 сервер это умел, а
  // кнопки не было нигде, и забывший фразу чинился только командой на сервере.
  const фразаЧеловека = useMutation({
    mutationFn: ({ id, фраза }: { id: string; фраза: string }) =>
      patchUser(id, { password: фраза }),
    onSuccess: () => {
      setПравимФразу(null);
      setНоваяФраза('');
      обновить();
    },
  });
  const входВПрограмму = useMutation({
    meta: подтверждение(
      'Вход в программу открыт',
      'человек сможет открыть программу на компьютере под своим именем и фразой',
    ),
    mutationFn: ({ id, впускать }: { id: string; впускать: boolean }) =>
      patchUser(id, { cabinet_access: впускать }),
    onMutate: () => setСбой(null),
    onError: (ошибка, переменные) => setСбой({ id: переменные.id, error: ошибка }),
    onSuccess: обновить,
  });
  const пачкаКодов = useMutation({
    mutationFn: (ids: string[]) => issueInvitesBulk(ids),
    onSuccess: (итог) => {
      setПачка(итог);
      setОтмечены([]);
      обновить();
    },
  });
  const карточкаЧеловека = useMutation({
    meta: подтверждение(
      'Учётная запись связана с карточкой',
      'человек увидит свои данные: расчётный лист, подотчёт',
    ),
    mutationFn: ({ id, employee_id }: { id: string; employee_id: string | null }) =>
      patchUser(id, { employee_id }),
    onMutate: () => setСбой(null),
    onError: (ошибка, переменные) => setСбой({ id: переменные.id, error: ошибка }),
    onSuccess: () => {
      setПравимКарточку(null);
      обновить();
    },
  });
  const отзыв = useMutation({
    mutationFn: (пользователь: UserOut) =>
      revokeInvite(пользователь.id).then((ответ) => ({
        id: пользователь.id,
        имя: пользователь.display_name,
        сколько: ответ.revoked,
      })),
    onMutate: () => setСбой(null),
    onError: (ошибка, пользователь) => setСбой({ id: пользователь.id, error: ошибка }),
    onSuccess: (итог) => {
      setКод(null);
      setОтозвано(итог);
      void обновить();
    },
  });
  const приглашение = useMutation({
    mutationFn: (пользователь: UserOut) =>
      issueInvite(пользователь.id).then((ответ) => ({
        id: пользователь.id,
        код: ответ.code,
        ссылка: ответ.link,
      })),
    onMutate: () => setСбой(null),
    onError: (ошибка, пользователь) => setСбой({ id: пользователь.id, error: ошибка }),
    onSuccess: (итог) => {
      setОтозвано(null);
      setКод(итог);
      void обновить();
    },
  });

  const удаление = useMutation({
    meta: подтверждение(
      'Учётная запись убрана из системы',
      'имя входа освободилось, код на руках больше не работает',
    ),
    mutationFn: (пользователь: UserOut) => deleteUser(пользователь.id),
    // Отказ удаления в общий `сбой` НЕ кладём, в отличие от остальных действий строки: окно
    // подтверждения остаётся открытым поверх строки, и одна и та же причина читалась бы
    // дважды — в окне и под ним. Человек в этот момент смотрит в окно, там ей и место.
    onSuccess: обновить,
  });

  // Блок тот же, что у соседних карточек настроек. Пока панель рисовала шапку сама, три
  // карточки на одном экране имели два разных заголовка: у двух подпись стояла в рамке шапки,
  // у третьей — внутри тела со значком.
  return (
    <Block
      title='Пользователи и права'
      note='Кто входит в систему и что ему видно'
      action={
        естьДействия ? (
          <div className='flex items-center gap-2'>
            {можноКоды && (
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => {
                  setРаздаём((было) => !было);
                  setОтмечены([]);
                  setПачка(null);
                }}
              >
                {раздаём ? 'Отменить раздачу' : 'Коды пачкой'}
              </Button.Root>
            )}
            {можноМенять && (
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => setОткрыта((было) => !было)}
              >
                <Button.Icon as={RiUserAddLine} />
                {открыта ? 'Отменить' : 'Завести человека'}
              </Button.Root>
            )}
          </div>
        ) : null
      }
    >
      {/* Один абзац вместо двух. Заказчик 13.09.2026: «как-то проще надо, чтобы всё было
          интуитивно понятно, без когнитивной нагрузки и шума». Над списком стояли два абзаца
          по три строки, и читать их приходилось каждый раз при входе в настройки. Осталось
          то, чего с экрана не видно: что двери две и что роли заменяются целиком. */}
      <p className='max-w-prose pb-3 text-paragraph-sm text-text-sub-600'>
        В программу на компьютере входят именем и фразой, в телефон — кодом. Роли заменяются
        целиком, снятая роль пропадает сразу.
      </p>

      {/* Сказать один раз наверху дешевле, чем показать девять кнопок, каждая из которых
          ответит красным отказом. Текст говорит ровно про ТО, чего человеку нельзя: с
          13.09.2026 прав здесь три, и общее «только просмотр» врало бы и руководителю, и
          операционному руководству — они и коды выдают, и доступы открывают. */}
      {!естьДействия && (
        <p className='max-w-prose pb-3 text-paragraph-sm text-text-sub-600'>
          Здесь только просмотр: людей заводит и доступы раздаёт администратор.
        </p>
      )}
      {естьДействия && !можноМенять && (
        <p className='max-w-prose pb-3 text-paragraph-sm text-text-sub-600'>
          Вам можно выдавать коды и открывать людям вход. Завести человека, сменить роль или
          фразу для входа может администратор.
        </p>
      )}

      {открыта && можноМенять && (
        <ФормаЗаведения
          роли={роли.data ?? []}
          onГотово={() => {
            setОткрыта(false);
            void обновить();
          }}
        />
      )}

      {раздаём && (
        <div className='mb-3 flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'>
          <p className='max-w-prose text-paragraph-sm text-text-sub-600'>
            Отметьте, кому раздаём коды. Полный код каждого показывается ОДИН раз — вот в этом
            списке; после ухода с экрана его не восстановить, поэтому список печатают или
            переписывают сразу. Код живёт трое суток.
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='xsmall'
              disabled={отмечены.length === 0 || пачкаКодов.isPending}
              onClick={() => пачкаКодов.mutate(отмечены)}
            >
              {пачкаКодов.isPending
                ? 'Выдаём…'
                : `Выдать коды (${отмечены.length})`}
            </Button.Root>
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xsmall'
              onClick={() =>
                setОтмечены(
                  (пользователи.data?.items ?? [])
                    .filter((человек) => человек.is_active)
                    .map((человек) => человек.id),
                )
              }
            >
              Отметить всех с открытым доступом
            </Button.Root>
          </div>
          {пачкаКодов.error !== null && <ErrorAlert error={пачкаКодов.error as ApiError} />}
        </div>
      )}

      {пачка !== null && (
        <div
          data-печать='документ'
          className='mb-3 flex flex-col gap-2 rounded-lg border border-stroke-soft-200 px-3 py-3'
        >
          <div className='flex items-center justify-between gap-2'>
            <span className='text-label-sm text-text-strong-950'>
              Коды приглашения: выдано {пачка.issued}
              {пачка.skipped > 0 && `, пропущено ${пачка.skipped}`}
            </span>
            <PrintButton размер='xxsmall' безЗначка скрытьПриПечати />
          </div>
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {пачка.rows.map((строка) => (
              <li
                key={строка.user_id}
                className='flex flex-wrap items-baseline justify-between gap-2 py-1'
              >
                <span className='text-paragraph-sm text-text-strong-950'>
                  {строка.display_name}
                  <span className='text-text-sub-600'> · вход «{строка.login}»</span>
                </span>
                {строка.code === null ? (
                  <span className='text-paragraph-sm text-warning-base'>
                    {строка.skipped_reason}
                  </span>
                ) : (
                  <span className='text-label-sm tracking-widest text-text-strong-950'>
                    {строка.code}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Ответы на действия над строкой — отказ и итог отзыва — рисуются ВНУТРИ строки, ниже
          по файлу. Наверху остаются только те, у которых своей строки нет: раздача пачкой и
          форма заведения. */}

      <УдалениеУчётнойЗаписи
        кого={удаляем}
        идёт={удаление.isPending}
        ошибка={удаление.error}
        закрыть={() => {
          setУдаляем(null);
          удаление.reset();
        }}
        убрать={() => {
          if (удаляем !== null) {
            удаление.mutate(удаляем, { onSuccess: () => setУдаляем(null) });
          }
        }}
      />

      <ScreenState
        data={пользователи.data}
        error={пользователи.error as never}
        subject='список пользователей'
        isEmpty={(значение) => значение.items.length === 0}
        // Каркас, а не строка текста. Это был единственный экран продукта, где загрузка
        // показывалась фразой: везде в кабинете на её месте стоит каркас той формы, что
        // грузится, и человек видит, СКОЛЬКО примерно приедет. Строка «Список пользователей
        // едет…» вдобавок прыгает высотой в момент появления списка.
        loading={<ListSkeleton rows={4} />}
        empty={
          <Hint.Root>
            Пользователей нет. Первого администратора заводят при установке, остальных — кнопкой
            выше.
          </Hint.Root>
        }
        onRetry={() => void пользователи.refetch()}
      >
        {(значение) => (
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {значение.items.map((пользователь) => (
              <li key={пользователь.id} className='flex flex-col gap-2 py-3'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <div className='flex items-center gap-2'>
                    {раздаём && можноМенять && (
                      <Checkbox.Root
                        id={`пачка-${пользователь.id}`}
                        checked={отмечены.includes(пользователь.id)}
                        onCheckedChange={() =>
                          setОтмечены((было) =>
                            было.includes(пользователь.id)
                              ? было.filter((идент) => идент !== пользователь.id)
                              : [...было, пользователь.id],
                          )
                        }
                      />
                    )}
                  <div className='flex flex-col'>
                    <span className='text-label-sm text-text-strong-950'>
                      {пользователь.display_name}
                      {убрана(пользователь)
                        ? ` · убрана ${formatDateOnly(пользователь.archived_at)}`
                        : !пользователь.is_active && ' · доступ закрыт'}
                    </span>
                    <span className='text-paragraph-xs text-text-sub-600'>
                      вход «{пользователь.login}»
                      {пользователь.employee_name !== null &&
                      пользователь.employee_name !== undefined
                        ? ` · ${пользователь.employee_name}`
                        : ' · нет карточки'}
                      {!пользователь.has_password && ' · фраза не задана'}
                      {/* Пишем только ИСКЛЮЧЕНИЕ. Доступ в программу выдают поштучно, и у
                          заказчика он есть у двоих из двадцати: надпись «только телефон» в
                          остальных восемнадцати строках — постоянный фон, который глаз всё
                          равно перестаёт читать, а место занимает. */}
                      {пользователь.cabinet_access && ' · входит в программу'}
                      {/* Про код в строке сказано ОДНО и короткое: человек им ещё не вошёл.
                          Хвоста «…W6» здесь больше нет — он обещал, что код где-то лежит и его
                          можно открыть, и заказчик 13.09.2026 именно этого и ждал. Объяснять
                          устройство хранения тут нечего: человеку нужен код, а не рассказ. */}
                      {пользователь.invite_live && ' · код выдан, ещё не входил'}
                    </span>
                  </div>
                  </div>
                  {/* Две группы с увеличенным зазором между ними, а не единый ряд из семи
                      кнопок: до 13.09.2026 «Код для приложения» стоял третьим в ряду, между
                      карточкой сотрудника и входом в программу, и по виду ничем от них не
                      отличался. Своей поверхности под группу не завожу — граница держится
                      зазором и значком (`.claude/rules/ui-views.md`). */}
                  {/* Снаружи РОВНО ОДНО действие, остальные под «Ещё».

                      13.09.2026 заказчик сказал, что процесс сложный и «надо проще». Он прав, и
                      дело было не в словах: в строке каждого человека стояли восемь кнопок
                      подряд, а у него в списке два десятка человек. Из восьми он нажимает одну
                      и то не каждый день — дать человеку код. Остальные семь он читает каждый
                      раз, когда ищет глазами нужную строку.

                      У убранной записи не рисуется ничего: она не правится и не возвращается
                      этим экраном, а кнопки над ней обещали бы обратное. Именно НЕ рисуется, а
                      не прячется стилем: спрятанная кнопка остаётся в разметке, её находит
                      чтение с экрана и ловит палец на планшете. */}
                  {естьДействия && !убрана(пользователь) && (
                    <div className='flex flex-wrap items-center gap-2'>
                      {можноКоды && (
                        <Button.Root
                          type='button'
                          variant='neutral'
                          mode='stroke'
                          size='xxsmall'
                          onClick={() => {
                            // Кнопка НЕ сереет, а объясняет и открывает следующий шаг:
                            // выключенная не доводит нажатие до обработчика, и человек,
                            // нажавший её без ответа, читает это как сломанную программу.
                            if (пользователь.roles.length === 0) {
                              setКод(null);
                              setСначалаРоль({
                                id: пользователь.id,
                                текст: можноМенять
                                  ? 'Сначала роль: без неё человек войдёт и не увидит ничего. Отметьте роль ниже — и нажмите «Дать код» ещё раз.'
                                  : 'У человека нет ни одной роли: код пустит его туда, где ничего не видно. Роль назначает администратор или финансовый директор.',
                              });
                              if (можноМенять) {
                                setПравимРоли(пользователь.id);
                              }
                              return;
                            }
                            setСначалаРоль(null);
                            приглашение.mutate(пользователь);
                          }}
                        >
                          <Button.Icon as={RiSmartphoneLine} />
                          {/* Честное слово вместо удобного. «Показать код» обещало, что код
                              где-то лежит и его можно открыть; на деле нажатие ВЫДАЁТ новый и
                              гасит прежний — и человек, нажавший его у того, у кого код уже на
                              руках, ломал человеку вход, не поняв этого. Кода в системе нет:
                              в базе лежит отпечаток. */}
                          {пользователь.invite_live ? 'Заменить код' : 'Дать код'}
                        </Button.Root>
                      )}
                      <МенюДействий
                        подпись={пользователь.display_name}
                        пункты={[
                          // Кто это и что видит: работа администратора.
                          ...(можноМенять
                            ? [
                                {
                                  группа: 'Кто это и что видит',
                                  подпись:
                                    правимРоли === пользователь.id
                                      ? 'Свернуть роли'
                                      : 'Изменить роли',
                                  выполнить: () =>
                                    setПравимРоли((прежний) =>
                                      прежний === пользователь.id ? null : пользователь.id,
                                    ),
                                },
                                {
                                  группа: 'Кто это и что видит',
                                  подпись:
                                    правимКарточку === пользователь.id
                                      ? 'Свернуть карточку'
                                      : пользователь.employee_id === null ||
                                          пользователь.employee_id === undefined
                                        ? 'Связать с карточкой сотрудника'
                                        : 'Сменить карточку сотрудника',
                                  выполнить: () =>
                                    setПравимКарточку((прежний) =>
                                      прежний === пользователь.id ? null : пользователь.id,
                                    ),
                                },
                                {
                                  группа: 'Как входит',
                                  подпись: пользователь.has_password
                                    ? 'Сменить фразу для программы'
                                    : 'Задать фразу для программы',
                                  выполнить: () => setПравимФразу(пользователь.id),
                                },
                              ]
                            : []),
                          // Как входит: доступы раздают ещё и руководитель с оперруком.
                          ...(можноДоступ
                            ? [
                                {
                                  группа: 'Как входит',
                                  подпись: пользователь.cabinet_access
                                    ? 'Закрыть вход в программу'
                                    : 'Пустить в программу',
                                  выполнить: () =>
                                    входВПрограмму.mutate({
                                      id: пользователь.id,
                                      впускать: !пользователь.cabinet_access,
                                    }),
                                },
                              ]
                            : []),
                          // Отзыв — только когда есть что отзывать. Пункт, который в девяти
                          // случаях из десяти отвечает «отзывать нечего», это шум.
                          ...(можноКоды && пользователь.invite_live
                            ? [
                                {
                                  группа: 'Как входит',
                                  подпись: 'Погасить код телефона',
                                  выполнить: () => отзыв.mutate(пользователь),
                                },
                              ]
                            : []),
                          // Убрать. Себя в этой группе нет вовсе, и это ответ на вопрос
                          // заказчика 13.09.2026: «если я нечаянно удалю сам себя, как зайти
                          // тогда?». Сервер такое и так запрещает тремя заслонами, но
                          // показанная кнопка сначала обещает, а потом отказывает — страх
                          // остаётся. Проще не показывать: своей записи человек не закрывает
                          // и не убирает ни при каких правах.
                          ...(можноДоступ && пользователь.id !== мойИдент
                            ? [
                                {
                                  группа: 'Убрать',
                                  подпись: пользователь.is_active
                                    ? 'Отключить: человек вернётся'
                                    : 'Включить обратно',
                                  выполнить: () =>
                                    включение.mutate({
                                      id: пользователь.id,
                                      active: !пользователь.is_active,
                                    }),
                                  опасное: пользователь.is_active,
                                },
                              ]
                            : []),
                          ...(можноУдалять && пользователь.id !== мойИдент
                            ? [
                                {
                                  группа: 'Убрать',
                                  подпись: 'Удалить насовсем',
                                  выполнить: () => setУдаляем(пользователь),
                                  опасное: true,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </div>
                  )}
                </div>

                {/* Отказ действия и итог отзыва — в строке того, у кого нажали. */}
                {сбой !== null && сбой.id === пользователь.id && (
                  <ErrorAlert error={сбой.error as ApiError} />
                )}
                {сначалаРоль !== null && сначалаРоль.id === пользователь.id && (
                  <Hint.Root hasError>{сначалаРоль.текст}</Hint.Root>
                )}
                {отозвано !== null && отозвано.id === пользователь.id && (
                  <div className='flex flex-col gap-1 rounded-lg bg-bg-weak-50 px-3 py-2'>
                    <span className='text-label-sm text-text-strong-950'>
                      {отозвано.сколько > 0 ? 'Код отозван' : 'Отзывать было нечего'}
                    </span>
                    {/* Ноль отозванных — это ответ, а не ошибка, и молчать о нём нельзя:
                        человек прочитал бы молчание как «код погашен», а код мог быть уже
                        использован — тогда вход у человека есть, и отзывом кода его не
                        забрать. */}
                    <span className='text-paragraph-xs text-text-sub-600'>
                      {отозвано.сколько > 0
                        ? 'Прежний код больше не работает. Чтобы человек вошёл, выпустите новый.'
                        : 'Живого кода не было: либо им уже вошли, либо его не выдавали. Если надо забрать доступ у вошедшего — закройте доступ.'}
                    </span>
                  </div>
                )}

                {/* Выданный код живёт ЗДЕСЬ, в строке того человека, кому его выдали.
                    Он держится, пока человек не закроет его сам: показать код второй раз нельзя
                    ни нам, ни серверу — в базе лежит отпечаток, а не код. */}
                {код !== null && код.id === пользователь.id && (
                  <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-2'>
                    <span className='text-label-sm text-text-strong-950'>
                      Код для телефона: <span className='tracking-widest'>{код.код}</span>
                    </span>
                    {/* Одна строка, а не четыре. Здесь человек хочет отдать код и уйти, а не
                        читать про устройство хранения и про две двери: про двери сказано один
                        раз наверху панели, и повторять это в каждой выдаче значит требовать
                        читать одно и то же по двадцать раз в день. */}
                    <span className='text-paragraph-xs text-text-sub-600'>
                      Пришлите ссылку: по ней человек войдёт одним нажатием, не набирая знаки.
                      Код годен трое суток. Если код выдавали раньше, прежний больше не
                      работает.
                    </span>
                    <div className='flex flex-wrap items-center gap-2'>
                      <КнопкаКопирования что={код.ссылка} подпись='Скопировать ссылку' вид='filled' />
                      <КнопкаКопирования что={код.код} подпись='Скопировать код' />
                      <Button.Root
                        type='button'
                        variant='neutral'
                        mode='ghost'
                        size='xxsmall'
                        onClick={() => setКод(null)}
                      >
                        Понятно
                      </Button.Root>
                    </div>
                  </div>
                )}

                {/* Роли ЧИТАЮТСЯ словами, а правятся по требованию.
                    До 26.08.2026 сетка галочек стояла всегда и у каждого: девять галочек на
                    человека, из них отмечена обычно одна. На снимке живого экрана четыре
                    человека давали тридцать шесть галочек, из которых тридцать две пустые, —
                    и один факт «он табельщик» приходилось вычитывать из этой ряби. Роли меняют
                    раз в жизни человека, а читают их каждый раз, когда открывают настройки. */}
                {правимРоли === пользователь.id ? null : (
                  <p className='text-paragraph-sm text-text-sub-600'>
                    {пользователь.roles.length === 0 ? (
                      // Короткая пометка, а не фраза. Развёрнутое объяснение стоит ОДИН раз
                      // над списком: сквозная проверка «один факт — один раз» поймала его же в
                      // шести строках подряд — ровно та рябь, ради которой проверка заведена.
                      <span className='text-warning-base'>Ролей нет</span>
                    ) : (
                      <>
                        Роли:{' '}
                        {(роли.data ?? [])
                          .filter((роль) => пользователь.roles.includes(роль.code))
                          .map((роль) => роль.title)
                          .join(', ')}
                      </>
                    )}
                  </p>
                )}

                {/* Галочка взята из дизайн-системы: своя была на три с половиной точки и попасть
                    в неё мышью можно было только с прицеливанием. */}
                <div
                  className={cn(
                    'grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:grid-cols-4',
                    правимРоли === пользователь.id ? '' : 'hidden',
                  )}
                >
                  {(роли.data ?? []).map((роль) => {
                    const есть = пользователь.roles.includes(роль.code);
                    return (
                      <label
                        key={роль.code}
                        htmlFor={`роль-${пользователь.id}-${роль.code}`}
                        className='flex cursor-pointer items-center gap-2 text-paragraph-sm text-text-sub-600'
                      >
                        <Checkbox.Root
                          id={`роль-${пользователь.id}-${роль.code}`}
                          checked={есть}
                          onCheckedChange={() =>
                            назначение.mutate({
                              id: пользователь.id,
                              roles: есть
                                ? пользователь.roles.filter((код) => код !== роль.code)
                                : [...пользователь.roles, роль.code],
                            })
                          }
                        />
                        {роль.title}
                      </label>
                    );
                  })}
                </div>

                {/* Служебные переписки.
                    Раскрываются той же кнопкой, что и роли: выдают их одним заходом — завёл
                    человека, дал роль, решил, какие переписки ему нужны. Отдельная кнопка
                    означала бы ещё одно место, куда надо не забыть зайти. */}
                <div className={cn(правимРоли === пользователь.id ? '' : 'hidden')}>
                  <div className='mt-3 text-paragraph-sm text-text-sub-600'>
                    Переписки, которые приходят сами. Выключены, пока не включите: право на
                    данные их не открывает. Помощника это не касается — он отвечает, когда его
                    спросят, и есть у всех.
                  </div>
                  <div className='mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3'>
                    {СЛУЖЕБНЫЕ_ПЕРЕПИСКИ.map((переписка) => {
                      const включена = (пользователь.bot_chats ?? []).includes(переписка.код);
                      return (
                        <label
                          key={переписка.код}
                          htmlFor={`чат-${пользователь.id}-${переписка.код}`}
                          className='flex cursor-pointer items-center gap-2 text-paragraph-sm text-text-sub-600'
                        >
                          <Checkbox.Root
                            id={`чат-${пользователь.id}-${переписка.код}`}
                            checked={включена}
                            onCheckedChange={() =>
                              перепискиЧеловека.mutate({
                                id: пользователь.id,
                                chats: включена
                                  ? (пользователь.bot_chats ?? []).filter(
                                      (код) => код !== переписка.код,
                                    )
                                  : [...(пользователь.bot_chats ?? []), переписка.код],
                              })
                            }
                          />
                          {переписка.название}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {правимФразу === пользователь.id && (
                  <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'>
                    <TextField
                      id={`фраза-${пользователь.id}`}
                      label={`Новая фраза для «${пользователь.login}»`}
                      value={новаяФраза}
                      onChange={setНоваяФраза}
                      type='text'
                      autoComplete='off'
                      maxLength={128}
                      hint='Не короче 12 знаков. Продиктуйте её человеку и попросите сменить на свою: «Мой вход» под его именем внизу слева.'
                    />
                    {фразаЧеловека.isError && <ErrorAlert error={фразаЧеловека.error} />}
                    <div className='flex gap-2'>
                      <Button.Root
                        type='button'
                        variant='primary'
                        mode='filled'
                        size='xxsmall'
                        disabled={новаяФраза.length < 12}
                        onClick={() =>
                          фразаЧеловека.mutate({ id: пользователь.id, фраза: новаяФраза })
                        }
                      >
                        {фразаЧеловека.isPending ? 'Задаём…' : 'Задать фразу'}
                      </Button.Root>
                      <Button.Root
                        type='button'
                        variant='neutral'
                        mode='ghost'
                        size='xxsmall'
                        onClick={() => {
                          setПравимФразу(null);
                          setНоваяФраза('');
                          фразаЧеловека.reset();
                        }}
                      >
                        Отмена
                      </Button.Root>
                    </div>
                  </div>
                )}

                {правимКарточку === пользователь.id && (
                  <div className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'>
                    <ВыборКарточки
                      id={`карточка-${пользователь.id}`}
                      значение={пользователь.employee_id ?? NO_VALUE}
                      onВыбор={(employee_id) =>
                        карточкаЧеловека.mutate({
                          id: пользователь.id,
                          employee_id: employee_id === NO_VALUE ? null : employee_id,
                        })
                      }
                      hint='По этой связи человек видит СВОИ данные: расчётный лист охраннику, подотчёт старшему. Без неё он войдёт и не увидит ничего.'
                    />
                    {(пользователь.employee_id ?? null) !== null && (
                      <div>
                        <Button.Root
                          type='button'
                          variant='neutral'
                          mode='ghost'
                          size='xxsmall'
                          onClick={() =>
                            карточкаЧеловека.mutate({
                              id: пользователь.id,
                              employee_id: null,
                            })
                          }
                        >
                          Отвязать карточку
                        </Button.Root>
                      </div>
                    )}
                  </div>
                )}

                {пользователь.roles.includes('field_manager') && можноМенять && (
                  <div className='flex flex-wrap gap-2'>
                    <span className='text-paragraph-xs text-text-sub-600'>Объекты:</span>
                    {(объекты.data?.items ?? []).map((объект) => {
                      const открыт = пользователь.site_ids.includes(объект.id);
                      return (
                        <CheckboxField
                          key={объект.id}
                          id={`объект-${пользователь.id}-${объект.id}`}
                          label={объект.name}
                          checked={открыт}
                          onCheckedChange={() =>
                            объектыЧеловека.mutate({
                              id: пользователь.id,
                              sites: открыт
                                ? пользователь.site_ids.filter((идент) => идент !== объект.id)
                                : [...пользователь.site_ids, объект.id],
                            })
                          }
                        />
                      );
                    })}
                    {(объекты.data?.items ?? []).length === 0 && (
                      <span className='text-paragraph-xs text-text-sub-600'>
                        объектов в справочнике нет — открывать нечего
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </ScreenState>

      {/* «Удалил не того» обязано чиниться экраном, а не звонком нам — но смотреть убранных
          надо редко, и в шапке блока эта кнопка была третьей: на планшете из-за неё обрезался
          сам заголовок раздела. Под списком она никому не мешает и находится, когда нужна. */}
      {можноУдалять && (
        <button
          type='button'
          onClick={() => setПоказатьУбранных((было) => !было)}
          // Высота честная, а не расширенная классом «цель-тача»: он рисует невидимый
          // прямоугольник 44 точки ПО ЦЕНТРУ, и у низкой строки этот прямоугольник вылезает
          // за её пределы и перехватывает нажатия по соседям. Ровно на этом 13.09.2026
          // перестали нажиматься кнопки «Ещё» на планшете.
          className='mt-3 inline-flex min-h-11 items-center self-start text-paragraph-xs text-text-sub-600 underline-offset-2 hover:underline'
        >
          {показатьУбранных ? 'Скрыть убранных' : 'Показать убранных'}
        </button>
      )}
    </Block>
  );
}

function ФормаЗаведения({ роли, onГотово }: { роли: RoleOut[]; onГотово: () => void }) {
  const [логин, setЛогин] = useState('');
  const [имя, setИмя] = useState('');
  const [фраза, setФраза] = useState('');
  const [выбранные, setВыбранные] = useState<string[]>([]);
  const [карточка, setКарточка] = useState(NO_VALUE);
  const [вПрограмму, setВПрограмму] = useState(false);

  const заведение = useMutation({
    meta: подтверждение('Учётная запись заведена'),
    mutationFn: () =>
      createUser({
        login: логин.trim(),
        display_name: имя.trim(),
        roles: выбранные,
        // Связь с карточкой ставится СРАЗУ, а не «потом когда-нибудь»: без неё человек войдёт
        // и не увидит ни своего расчётного листа, ни своего подотчёта, а понять почему —
        // нечем. Поле необязательное: администратор и финотдел работают без карточки.
        employee_id: карточка === NO_VALUE ? null : карточка,
        password: фраза === '' ? undefined : фраза,
        cabinet_access: вПрограмму,
      }),
    onSuccess: () => {
      setЛогин('');
      setИмя('');
      setФраза('');
      setВыбранные([]);
      setКарточка(NO_VALUE);
      setВПрограмму(false);
      onГотово();
    },
  });

  return (
    <form
      className='flex flex-col gap-2 rounded-lg bg-bg-weak-50 px-3 py-3'
      onSubmit={(событие) => {
        событие.preventDefault();
        заведение.mutate();
      }}
    >
      <TextField
        id='new-user-login'
        label='Имя входа'
        value={логин}
        onChange={setЛогин}
        hint='Латиницей или кириллицей, без пробелов. Его человек набирает при входе.'
      />
      <TextField
        id='new-user-name'
        label='Как показывать'
        value={имя}
        onChange={setИмя}
        hint='Фамилия и имя: их видно в журнале действий и в очередях.'
      />
      <TextField
        id='new-user-secret'
        label='Фраза для входа'
        value={фраза}
        onChange={setФраза}
        hint='Для программы на компьютере. В приложение человек входит по коду, а не по фразе.'
      />
      {/* Отдельная галочка, а не следствие заданной фразы. Программу открывают финансовый и
          генеральный директор; остальным доступ выдают поштучно и осознанно — до 09.09.2026
          его открывала сама фраза, и учётная запись охранника, заведённая под демонстрацию
          для магазина приложений, открывала кабинет со всеми деньгами. */}
      <CheckboxField
        id='new-user-cabinet'
        label='Может входить в программу на компьютере'
        checked={вПрограмму}
        onCheckedChange={() => setВПрограмму((было) => !было)}
      />
      <ВыборКарточки
        id='new-user-employee'
        значение={карточка}
        onВыбор={setКарточка}
        hint='Без карточки человек не увидит своего расчётного листа и своего подотчёта. Администратору и финансовому отделу она не нужна.'
      />
      <div className='flex flex-wrap gap-2'>
        {роли.map((роль) => (
          <CheckboxField
            key={роль.code}
            id={`новая-роль-${роль.code}`}
            label={роль.title}
            checked={выбранные.includes(роль.code)}
            onCheckedChange={() =>
              setВыбранные((было) =>
                было.includes(роль.code)
                  ? было.filter((код) => код !== роль.code)
                  : [...было, роль.code],
              )
            }
          />
        ))}
      </div>
      {заведение.error !== null && <ErrorAlert error={заведение.error as ApiError} />}
      <div>
        <Button.Root type='submit' variant='primary' mode='filled' size='xsmall'>
          {заведение.isPending ? 'Заводим…' : 'Завести'}
        </Button.Root>
      </div>
    </form>
  );
}
